import { I18n } from "@assets/strings";
import React, { useRef, useState } from "react";
import { useHistory } from "@core/routing/useHistory";
import styled, { css } from "styled-components";
import { Path } from "@core/routing/path";
import { Post } from "@domain/post/post";
import { formatTimeAgo } from "@utils/date";
import { scrollToId } from "@utils/dom";
import { useIsMobile } from "@utils/hooks";
import { gap } from "@ui/styles/utils";
import { useAuthModalBlocker } from "@ui/components/auth/useAuthModalBlocker";
import { DraftRenderer } from "@ui/components/containers/draftRenderer";
import { RoundedBlock } from "@ui/components/containers/roundedBlock";
import { Stack } from "@ui/components/layout";
import { AuthLink } from "@ui/components/linkExternal";
import { KebabMenu } from "@ui/components/menus/kebabMenu";
import { UserView, UserViewSkeleton } from "@ui/components/profile/userView";
import { CommentInput } from "@ui/components/post/comment/commentInput";
import { PostCommentSkeleton } from "@ui/components/post/comment/commentView";
import { ParentCommentView } from "@ui/components/post/comment/parentCommentView";
import { PostCommentsList } from "./postCommentsList";
import { PostContent } from "./postContent";
import { PostContentDeleted } from "./postCourseDeleted";
import { PostMenu } from "./postMenu";

interface PostViewProps extends React.HTMLAttributes<HTMLDivElement> {
	post: Post;
}
export const PostView: React.FC<PostViewProps> = ({ post, ...htmlProps }) => {
	const blockGuest = useAuthModalBlocker();
	const displayedPost = post.type === "SHARE_POST" ? post.reference : post;
	const [commentVisibles, setCommentVisibles] = useState(false);
	const isMobile = useIsMobile();
	const history = useHistory();
	const commentRef = useRef<{ focus: () => void }>(null);
	const showComments = (): void =>
		blockGuest(() => {
			if (isMobile && displayedPost) {
				history.push(Path.Post(displayedPost.id));
			} else {
				setCommentVisibles(true);
			}
		});
	const isShared = post.type === "SHARE_POST";

	return (
		<Container isShared={isShared} data-id={post.id} {...htmlProps}>
			{isShared && (
				<ShareInfo>
					<Author>
						<StyledUserView
							pictureSize={"M"}
							user={post.author}
							showDescription={!post.group}
							alignStart
							unClickable={true}
							renderBottomAccessory={() => <PostDate> {formatTimeAgo(post.creationDate)}</PostDate>}
						>
							<PostAction>
								{" "}
								{I18n.t("post.shared")}{" "}
								{post.group && (
									<AuthLink to={Path.Discussion(post.group)}>
										<b>{I18n.t("post.group.infos", { group: post.group.name })}</b>
									</AuthLink>
								)}
							</PostAction>
						</StyledUserView>
						<Grow />
						<KebabMenu closeOnSelect authOnly>
							<PostMenu post={post} />
						</KebabMenu>
					</Author>
					{post.text.hasText() && <ShareText fold content={post.text} />}
				</ShareInfo>
			)}
			{displayedPost ? (
				<PostContainer>
					<Author>
						<StyledUserView
							pictureSize={"M"}
							user={displayedPost.author}
							showDescription={true}
							alignStart
							renderBottomAccessory={() => <PostDate> {formatTimeAgo(displayedPost.creationDate)}</PostDate>}
						>
							<PostAction>
								{" "}
								{post.group ? (
									post.type !== "SHARE_LIVE_COURSE" ? (
										<AuthLink to={Path.Discussion(post.group)}>
											{I18n.t("post.group.published")} <b>{post.group.name}</b>
										</AuthLink>
									) : post.author.id === post.reference?.author?.id ? (
										<AuthLink to={Path.Discussion(post.group)}>
											{I18n.t("post.livecourse_published")} <b>{post.group.name}</b>
										</AuthLink>
									) : (
										<AuthLink to={Path.Discussion(post.group)}>
											{I18n.t("post.livecourse_shared")} <b>{post.group.name}</b>
										</AuthLink>
									)
								) : post.type === "SHARE_LIVE_COURSE" ? (
									post.author.id === post.reference?.author?.id ? (
										I18n.t("post.livecourse_published")
									) : (
										I18n.t("post.livecourse_shared")
									)
								) : post.type === "SHARE_APPLICATION" ? (
									I18n.t("post.application.published")
								) : post.type === "SHARE_CAROUSEL" ? (
									post.author.id === post.reference?.author?.id ? (
										I18n.t("post.carousel_published")
									) : (
										I18n.t("post.carousel_shared")
									)
								) : post.type === "SHARE_COURSE" ? (
									post.author.id === post.reference?.author?.id ? (
										I18n.t("post.course_published")
									) : (
										I18n.t("post.course_shared")
									)
								) : post.type === "SHARE_QUIZ" ? (
									post.author.id === post.reference?.author?.id ? (
										I18n.t("post.quiz_published")
									) : (
										I18n.t("post.quiz_shared")
									)
								) : post.type === "SHARE_ARTICLE" ? (
									post.author.id === post.reference?.author?.id ? (
										I18n.t("post.article_published")
									) : (
										I18n.t("post.article_shared")
									)
								) : undefined}
								{post.type === "SHARE_CAROUSEL" && <b>{I18n.t(`category.${post.reference?.category}`)}</b>}
							</PostAction>
						</StyledUserView>
						<Grow />
						<KebabMenu closeOnSelect authOnly>
							<PostMenu post={displayedPost} />
						</KebabMenu>
					</Author>

					<Content post={displayedPost} onCommentAction={() => commentRef.current?.focus()} />

					<Separator />
					{displayedPost.comments.last && !commentVisibles && (
						<TeasingComment postComment={displayedPost.comments.last} postId={displayedPost.id} />
					)}
					{displayedPost.comments.total > 1 && !commentVisibles && (
						<ShowComments onClick={showComments}>
							{I18n.t("post.comment.show", { count: displayedPost.comments.total })}
						</ShowComments>
					)}
					<CommentInput
						ref={commentRef}
						postId={displayedPost.id}
						onCommentSubmit={async (comment) => {
							showComments();
							if (isMobile) {
								return;
							}
							if (comment) {
								scrollToId(comment.id);
							}
						}}
					/>
					{commentVisibles && displayedPost && displayedPost.comments ? (
						<CommentsList postId={displayedPost.id} initialComment={displayedPost.comments.last} />
					) : null}
				</PostContainer>
			) : (
				<PostContentDeleted type="post" />
			)}
		</Container>
	);
};

export const PostViewSkeleton: React.FC<React.HTMLAttributes<HTMLDivElement>> = (htmlProps) => (
	<Container {...htmlProps}>
		<PostContainer>
			<Stack gap={10} align="stretch">
				<UserViewSkeleton pictureSize={"M"} alignStart />
				<div style={{ height: 100 }} />
				<Separator />
				<PostCommentSkeleton />
			</Stack>
		</PostContainer>
	</Container>
);

const Container = styled.div.withConfig({
	shouldForwardProp: (prop) => prop !== "isShared",
})<{ isShared?: boolean }>`
	${({ theme, isShared }) =>
		isShared &&
		css`
			${theme.containerStyles.roundedBlockStyle}
			${PostContainer} {
				margin: 0 -1px;
				box-shadow: 0px -4px 26px 0px rgba(0, 0, 0, 0.12);
			}
		`};
	border-bottom: none;
`;

const PostContainer = styled(RoundedBlock)`
	padding: 20px 18px;
`;

const ShareInfo = styled.div`
	padding: 20px 18px;
	${gap(10)};
`;

const ShareText = styled(DraftRenderer)`
	${({ theme }) => theme.textStyles.secondary};
`;

const Grow = styled.div`
	flex: 1;
`;

const Author = styled.div`
	display: flex;
	align-items: flex-start;
`;

const StyledUserView = styled(UserView)`
	font-size: 0.9375rem;
`;

const PostAction = styled.span`
	font-weight: 500;
`;

const PostDate = styled.span`
	${({ theme }) => theme.textStyles.tertiary};
	font-weight: 500;
`;

const Content = styled(PostContent)`
	margin-top: 10px;
`;

const Separator = styled.div`
	margin: 18px -18px;
	height: 1px;
	background-color: ${({ theme }) => theme.colors.lightergray};
`;

const ShowComments = styled.div`
	cursor: pointer;
	${({ theme }) => theme.textStyles.label};
	margin-bottom: 10px;
`;

const CommentsList = styled(PostCommentsList)`
	margin-top: 20px;
	background-color: ${({ theme }) => theme.colors.white};
	position: relative;
`;

const TeasingComment = styled(ParentCommentView)`
	margin-bottom: 10px;
`;
